import React, { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { GBP } from '../proposals/constants';
import CostEntryDialog from '../profitability/CostEntryDialog';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PlusCircle, Pencil, Trash2 } from 'lucide-react';

const TEAL = '#3BC6B4';

function Stat({ label, value, tint = 'text-[color:var(--bm-text)]' }) {
    return (
        <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">{label}</div>
            <div className={`font-display text-[18px] font-semibold mt-1 leading-none ${tint}`}>{value}</div>
        </div>
    );
}

export default function ClientProfitabilityTab({ clientId }) {
    const { can } = useAuth();
    const canCreate = can('finance', 'create');
    const canEdit = can('finance', 'edit');
    const canDelete = can('finance', 'delete');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogState, setDialogState] = useState(null); // { period, entry }
    const [editingOverride, setEditingOverride] = useState(false);
    const [overrideVal, setOverrideVal] = useState('');

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/profitability/clients/${clientId}`);
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    const saveOverride = async () => {
        const num = overrideVal === '' ? null : parseFloat(overrideVal);
        try {
            await api.patch(`/clients/${clientId}`, { profitability_forecast_override: num });
            toast.success('Forecast override saved');
            setEditingOverride(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    const clearOverride = async () => {
        try {
            await api.patch(`/clients/${clientId}`, { profitability_forecast_override: null });
            toast.success('Forecast override cleared — using average');
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    const deleteEntry = async (id) => {
        try {
            await api.delete(`/profitability/cost-entries/${id}`);
            toast.success('Cost entry deleted');
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    if (loading) return <div className="bg-white rounded-xl border border-[color:var(--bm-border)] px-5 py-8 text-center text-[color:var(--bm-text-muted)]">Loading profitability…</div>;
    if (!data) return null;

    return (
        <div className="space-y-5" data-testid="client-profitability-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Revenue to date" value={GBP(data.revenue_to_date)} />
                <Stat label="Cost to date" value={GBP(data.cost_to_date)} />
                <Stat label="GP to date" value={GBP(data.gp_to_date)} tint="text-[color:var(--bm-accent-600)]" />
                <Stat label="GP%" value={`${data.gp_pct}%`} tint="text-emerald-600" />
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div className="font-display text-[15px] font-semibold">Forecast</div>
                    {canCreate && (
                        <Button size="sm" onClick={() => setDialogState({ period: '', entry: null })} data-testid="client-log-cost-btn">
                            <PlusCircle size={14} className="mr-1.5" /> Log cost
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <Stat label="Trailing avg cost/mo" value={GBP(data.trailing_avg_cost)} />
                    <div className="bg-[color:var(--bm-bg-mute)] rounded-lg px-4 py-3">
                        <div className="text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold">Forecast £/mo</div>
                        {editingOverride ? (
                            <div className="flex items-center gap-1.5 mt-1.5">
                                <Input type="number" value={overrideVal} onChange={e => setOverrideVal(e.target.value)}
                                       className="h-7 text-[13px]" data-testid="client-forecast-override-input" autoFocus />
                                <Button size="sm" className="h-7 px-2 text-[11px]" style={{ background: 'var(--bm-primary)' }}
                                        onClick={saveOverride} data-testid="client-forecast-override-save">Save</Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 mt-1">
                                <button type="button" onClick={() => { if (!canEdit) return; setOverrideVal(data.forecast_monthly_cost ?? ''); setEditingOverride(true); }}
                                        className="flex items-center gap-1.5" data-testid="client-forecast-override-display">
                                    <span className="font-display text-[18px] font-semibold">{GBP(data.forecast_monthly_cost)}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${data.has_override ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                                        {data.has_override ? 'your figure' : 'using average'}
                                    </span>
                                    {canEdit && <Pencil size={11} className="opacity-50" />}
                                </button>
                                {canEdit && data.has_override && (
                                    <button type="button" onClick={clearOverride}
                                            className="text-[10.5px] text-[color:var(--bm-text-subtle)] hover:text-[color:var(--bm-primary)] underline"
                                            data-testid="client-forecast-override-clear">
                                        Use average instead
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                <div className="font-display text-[15px] font-semibold mb-4">Last 12 months (actual)</div>
                <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={data.monthly} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                        <defs>
                            <linearGradient id="gGp" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={TEAL} stopOpacity={0.25} />
                                <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E3E8ED" />
                        <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#8A95A3' }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={v => `£${v}`} tick={{ fontSize: 10, fill: '#8A95A3' }} axisLine={false} tickLine={false} width={50} />
                        <Tooltip formatter={(v) => GBP(v)} />
                        <Area type="monotone" dataKey="gp" stroke={TEAL} strokeWidth={2} fill="url(#gGp)" name="GP" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {data.active_schedules.length > 0 && (
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5">
                    <div className="font-display text-[15px] font-semibold mb-3">Active recurring schedules driving this forecast</div>
                    <div className="space-y-2">
                        {data.active_schedules.map(s => (
                            <div key={s.id} className="flex items-center justify-between text-[12.5px] px-3 py-2 rounded-lg bg-[color:var(--bm-bg-mute)]">
                                <span className="font-medium">{s.title}</span>
                                <span className="text-[color:var(--bm-text-muted)] capitalize">{s.frequency} · next {s.next_run_date}</span>
                                <span className="font-medium">{GBP(s.amount_per_run)}/run</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                <div className="font-display text-[15px] font-semibold px-5 py-4">Cost entries</div>
                <table className="w-full text-[12.5px]" data-testid="client-cost-entries-table">
                    <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                        <tr>
                            <th className="text-left py-2 px-5 font-medium">Month</th>
                            <th className="text-left py-2 px-5 font-medium">Basis</th>
                            <th className="text-right py-2 px-5 font-medium">Amount</th>
                            <th className="text-left py-2 px-5 font-medium">Note</th>
                            <th className="py-2 px-5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.cost_entries.map(e => (
                            <tr key={e.id} className="border-b border-[color:var(--bm-border)] last:border-0" data-testid={`client-cost-entry-${e.id}`}>
                                <td className="py-2.5 px-5 font-medium">{e.period}</td>
                                <td className="py-2.5 px-5 text-[color:var(--bm-text-muted)]">
                                    {e.entry_mode === 'credits' ? `${Math.round(e.credits_used).toLocaleString()} cr @ £${e.rate_applied}` : 'Direct £'}
                                </td>
                                <td className="py-2.5 px-5 text-right font-medium">{GBP(e.amount)}</td>
                                <td className="py-2.5 px-5 text-[color:var(--bm-text-muted)]">{e.note || '—'}</td>
                                <td className="py-2.5 px-5 text-right whitespace-nowrap">
                                    {canEdit && (
                                        <button onClick={() => setDialogState({ period: e.period, entry: e })} className="text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)] mr-2" data-testid={`edit-cost-entry-${e.id}`}>
                                            <Pencil size={13} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button onClick={() => deleteEntry(e.id)} className="text-[color:var(--bm-text-muted)] hover:text-red-600" data-testid={`delete-cost-entry-${e.id}`}>
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {data.cost_entries.length === 0 && (
                            <tr><td colSpan={5} className="py-8 text-center text-[color:var(--bm-text-muted)]">No costs logged for this client yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {dialogState && (
                <CostEntryDialog
                    open={!!dialogState} onOpenChange={(v) => { if (!v) setDialogState(null); }}
                    clientId={clientId} period={dialogState.entry ? dialogState.entry.period : (dialogState.period || undefined)}
                    entry={dialogState.entry} onSaved={() => { setDialogState(null); load(); }}
                />
            )}
        </div>
    );
}
